/**
 * Browser printing.
 *
 * The bill is written into a hidden same-origin iframe rather than a popup:
 * popups are blocked unless the click is trusted all the way down, and an
 * iframe keeps the till's own page — and the order in progress — untouched
 * while the print dialog is open.
 */
export async function printHtml(html: string): Promise<void> {
  if (typeof document === 'undefined') return;

  const frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.style.cssText =
    'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden';
  document.body.appendChild(frame);

  const done = () => {
    // Removing it synchronously from onafterprint races Safari, which is still
    // reading the document when the event fires.
    setTimeout(() => frame.remove(), 1000);
  };

  await new Promise<void>((resolve) => {
    frame.onload = () => {
      const win = frame.contentWindow;
      if (!win) {
        done();
        return resolve();
      }
      win.onafterprint = () => {
        done();
        resolve();
      };
      try {
        win.focus();
        win.print();
      } catch {
        done();
        resolve();
      }
      // Some browsers never fire onafterprint. Clean up regardless.
      setTimeout(() => {
        done();
        resolve();
      }, 60_000);
    };
    frame.srcdoc = html;
  });
}

export function canPrint(): boolean {
  return typeof document !== 'undefined' && typeof window !== 'undefined';
}
